import React, { useEffect, useState } from 'react';
import cn from 'classnames';
import { getMinimumAccessAge } from 'global-utils/age-utils';
import useLogoProportions from '@age-gateway/hooks/useLogoProportions';
import defaultLogo from '@age-gateway/assets/img/default_logo.svg';
import { isMuslinMarket } from '@age-gateway/utils/market-utils';
import countriesInfo from '@age-gateway/config/legal-ages.json';
import { getProjectInfo } from 'global-utils/get-attributes';
import trapsManager from 'global-utils/trapManager';
import FocusTrap from 'focus-trap-react';
import styles from './age-gateway.module.scss';
import Actions from './Actions';

const AgeGateway = ({
  logoUrl,
  brandName,
  setNoAccess,
  onCloseAgeGate,
  backgroundImageUrl,
  noConfig,
  translations
}: {
  logoUrl: string;
  brandName: string;
  setNoAccess: (hasAccess: boolean) => void;
  onCloseAgeGate: () => void;
  noConfig: boolean;
  backgroundImageUrl?: string;
  translations: {
    labels: { [key: string]: string };
    [key: string]: string | { [key: string]: string };
  };
}) => {
  const { prjMarket } = getProjectInfo();
  const isImageVersion = !!backgroundImageUrl;

  // if the plugin fails to define the user location, we should use the project market
  // as a legal drinking age reference instead
  const ageReference = prjMarket?.toUpperCase();

  const minimumAccessAge = getMinimumAccessAge(ageReference);
  const { isLogoHorizontal } = useLogoProportions(logoUrl);
  const { countriesList } = countriesInfo;
  const isCountryMuslin = isMuslinMarket(
    prjMarket?.toUpperCase(),
    countriesList
  );

  const trapId = 'ag-banner-trap';
  const priority = 2;

  const [isActive, setIsActive] = useState(false);

  useEffect(() => {
    // Register the trap on mount
    trapsManager?.registerTrap(trapId, priority);

    // Sync initial state with trapsManager
    const currentTrap = trapsManager?.traps.find((trap) => trap.id === trapId);
    if (currentTrap) {
      setIsActive(currentTrap.isActive);
    }

    // Subscribe to traps state changes
    const unsubscribe = trapsManager?.subscribe((traps) => {
      // eslint-disable-next-line @typescript-eslint/no-shadow
      const trap = traps.find((trap) => trap.id === trapId);
      setIsActive(trap?.isActive || false);
    });

    // Unregister the trap on unmount
    return () => {
      trapsManager?.unregisterTrap(trapId);
      unsubscribe?.();
    };
  }, []);

  const titleMessage = {
    __html: translations?.yearLabel
      ?.toString()
      .replace('{{consentAge}}', minimumAccessAge?.toString())
      .replace('<break></break>', '<br />')
  };

  const AGComponent = (
    <dialog
      data-trap-id={trapId}
      data-testid="age-gateway"
      className={cn(styles.root, { [styles.hasImage]: isImageVersion })}
    >
      <div
        className={
          (styles.layout, cn({ [styles.imageVersion]: isImageVersion }))
        }
      >
        <section
          className={cn(styles.content, {
            [styles.imageVersion]: isImageVersion
          })}
        >
          <div className={styles.innerContent}>
            {logoUrl && (
              <div
                className={styles.logo}
                // provides a height for the logo container if the logo is not horizontal
                style={isLogoHorizontal ? null : { height: '120px' }}
              >
                <img
                  src={logoUrl}
                  alt={`${brandName} logo`}
                  width="143"
                  height="68"
                  style={
                    isLogoHorizontal
                      ? { width: '100%', height: 'auto' }
                      : { height: '100%', width: 'auto' }
                  }
                />
              </div>
            )}
            {noConfig && (
              <div className={styles.logo}>
                <img
                  src={defaultLogo}
                  alt="carlsberg group logo"
                  style={{
                    width: '100%',
                    height: 'auto'
                  }}
                />
              </div>
            )}
            <h1
              className={styles.title}
              dangerouslySetInnerHTML={titleMessage}
            />

            {isCountryMuslin && (
              <p className={styles.confirmNotice}>
                {translations.alertForMuslims}
              </p>
            )}
            <Actions
              isImageVersion={isImageVersion}
              setNoAccess={setNoAccess}
              accessAge={minimumAccessAge}
              onCloseAgeGate={onCloseAgeGate}
              translations={translations}
            />
          </div>
        </section>
        {backgroundImageUrl && (
          <div className={cn(styles.image)}>
            <img
              src={backgroundImageUrl}
              alt="decorative background image for the age gateway banner"
              data-testid="background-image"
              width="620"
              height="413"
            />
          </div>
        )}
      </div>
    </dialog>
  );

  return <FocusTrap active={isActive}>{AGComponent}</FocusTrap>;
};

const MemoizedAgeGate = React.memo(AgeGateway);

export default MemoizedAgeGate;
